<template>
    <div class="property-search-form-wrapper">
        <div v-html="title"></div>
        <form novalidate @submit.stop.prevent="showBuildingView">
            <slot name="inline-title"/>
            <div>
                <div class="block">
                    <el-select v-model="input.beds" placeholder="Bedrooms" class="form-field">
                        <el-option
                            v-for="type in bedrooms"
                            :key="type.value"
                            :label="type.label"
                            :value="type.value"
                        >
                        </el-option>
                    </el-select>
                </div>
                <div class="block">
                    <el-select v-model="input.baths" placeholder="Bathrooms" class="form-field">
                        <el-option
                            v-for="type in bathrooms"
                            :key="type.value"
                            :label="type.label"
                            :value="type.value"
                        >
                        </el-option>
                    </el-select>
                </div>
                <div class="block">
                    <label class="lb-price-range">Monthly Rent Price Range</label>
                    <el-slider v-model="input.price" range :min="0" :max="10000" :format-tooltip="formatTooltip" class="form-field"/>
                    <div class="text-right">
                        <span>{{priceMin}}</span> - <span>{{priceMax}}</span>
                    </div>
                </div>
            </div>
            <button @click="showBuildingView" class="link-cta inline-block-i">
                View Results <font-awesome-icon class="building" :icon="icons.building"/>
            </button>
        </form>
    </div>
</template>
<script>
    import states from './states';
    import CustomInputMixin from '../../mixins/customInput.mixin.js';
    import currencyFormatter from 'currency-formatter';
    import _map from 'lodash/map';
    import _uniq from 'lodash/uniq';
    import _uniqBy from 'lodash/uniqBy';
    import _contains from 'lodash/includes';
    import _reject from 'lodash/reject';
    import {faBell} from '@fortawesome/pro-regular-svg-icons/fabell'
    import {faBuilding} from '@fortawesome/pro-light-svg-icons/faBuilding'

    export default {
        name: 'AptSearchTool',
        mixins: [CustomInputMixin],
        props: {
            properties: {
                type: Array,
                default: () => { return []; }
            },
            locations: {
                type: Array,
                default: () => { return [] }
            },
            hasSubmit: {
                default: true,
                types: Boolean
            }
        },
        data() {
            return {
                input: {
                    beds: '',
                    baths: '',
                    city: '',
                    date: '',
                    state: '',
                    zip: '',
                    price: [0, 9999],
                    amenities: []
                },
                states,
                enableWatcher: true
            }
        },
        watch: {
            state(newState, oldState) {
                if (newState !== oldState && this.enableWatcher) {
                    this.city = '';
                    this.$emit('change');
                }
            }
        },
        computed: {
            title() {
                return 'Search Options';
            },
            icons() {
                return {
                    bell: faBell,
                    building: faBuilding
                }
            },
            bathrooms() {
                let bathroomList = [
                    {
                        value: -1,
                        label: 'All'
                    }
                ];

                if (!this.properties) return bathroomList;

                let bathrooms = [];
                this.properties.forEach((property) => {
                    let apartmentTypes = property.acf.bathrooms;
                    apartmentTypes.forEach((type) => {
                        bathrooms.push(parseFloat(type));
                    });
                });
                bathrooms = _uniq(bathrooms).sort();

                bathrooms.forEach((bathroom) => {
                    bathroomList.push({
                        value: bathroom,
                        label: `${bathroom} Bathroom${bathroom > 1.5 ?'s':''}`
                    });
                });

                return bathroomList;
            },
            bedrooms() {
                let bedroomList = [
                    {
                        value: -1,
                        label: 'All'
                    }
                ];

                if (!this.properties) return bedroomList;

                let bedrooms = [];
                this.properties.forEach((property) => {
                    let apartmentTypes = property.acf.apartment_types;
                    apartmentTypes.forEach((type) => {
                        bedrooms.push(parseInt(type));
                    });
                });
                bedrooms = _uniq(bedrooms).sort();

                bedrooms.forEach((bedroom) => {
                    if (bedroom === 0) {
                        bedroomList.push({
                            value: 0,
                            label: 'Studio'
                        });
                    } else if (bedroom === 30) {
                        // note: I say TH in the old hardcoded array,
                        // not sure if 31 value will work for Den
                        bedroomList.push({
                            value: 'TH',
                            label: 'Townhouse'
                        });
                    } else if (bedroom === 31) {
                        // note: I say TH in the old hardcoded array,
                        // not sure if 31 value will work for Den
                        bedroomList.push({
                            value: 31,
                            label: 'Den'
                        });
                    } else {
                        bedroomList.push({
                            value: bedroom,
                            label: `${bedroom} Bedroom${bedroom === 1 ? '':'s'}`
                        });
                    }
                });

                return bedroomList;
            },
            amenities() {
                return [
                    {
                        value: 'pool',
                        label: 'Pool',
                    },
                    {
                        value: 'gym',
                        label: 'Gym',
                    },
                    {
                        value: 'pet_friendly',
                        label: 'Pet Friendly',
                    },
                    {
                        value: 'wifi',
                        label: 'Wifi',
                    },
                    {
                        value: 'laundry',
                        label: 'Laundry Facility',
                    },
                ]
            },
            postsStates() {
                // Get all posts states
                let states = _uniq(
                    _map(this.locations, (post) => {
                        return post.acf.state;
                    }), 'label');
                let mappedStates = _uniqBy(states, 'value');
                mappedStates.unshift({value: '*', label: 'All'});
                return mappedStates;
            },
            cities() {
                // Get all posts cities
                let mappedCities = _uniq(
                    _map(
                        _reject(this.locations, (post) => {
                            return !_contains(['', 'All', post.acf.state.value], this.input.state);
                        }), (post) => {
                            return post.acf.city
                        }
                    )
                );
                mappedCities.unshift('All');
                return mappedCities;
            },
            priceMin() {
                return `${this.formatCurrencyAmount(this.input.price[0])}`;
            },
            priceMax() {
                return `${this.formatCurrencyAmount(this.input.price[1])}`;
            }
        },
        methods: {
            handleCheckedAmenitiesChange(value) {
                let checkedCount = value.length;
                this.checkAll = checkedCount === this.amenities.length;
                this.isIndeterminate = checkedCount > 0 && checkedCount < this.amenities.length;
            },
            formatCurrencyAmount(amount) {
                return currencyFormatter.format(amount, {
                    code: 'USD',
                    symbol: '$',
                    thousandsSeparator: ',',
                    decimalSeparator: '.',
                    symbolOnLeft: true,
                    precision: 0,
                    spaceBetweenAmountAndSymbol: false,
                    decimalDigits: 0,
                });
            },
            formatTooltip(val) {
                return this.formatCurrencyAmount(val);
            },
            showBuildingView() {
                this.$emit('showBuildingView')
            }
        }
    }
</script>
<style lang="scss" type="text/scss">
    .property-search-form-wrapper {
        margin: 0 auto;
        max-width: 420px;
        color: #fff;
        padding: 30px 40px;
        font-size: 12px;
        font-family: inherit;

        label.lb-price-range {
            padding-left: 16px;
            font-size: 12px;
        }
        .el-slider__runway {
            height: 3px;
        }
        .el-slider__bar {
            background-color: $theme-green;
            height: 3px;
        }
        .el-slider__button {
            border: 0;
            height: 12px;
            width: 12px;
            transform: translateY(-1px);
        }
        .el-slider__button {
            width: 14px;
            height: 14px;
        }
        .link-cta {
            margin-top: 25px;
            background-image: linear-gradient(to bottom, $theme-green 0%, $theme-green 50%, $theme-green 100%);
            width: 100%;
            color: #fff;
            box-shadow: none;
            border: 0;
            border-radius: 0;
            height: 40px;
            cursor: pointer;
            transition: all .3s ease-in-out;
            font-size: 12px;
            line-height: 25px;

            &:hover {
                opacity: .8;
            }

            .building {
                margin-left: 10px;
            }

            @media (min-width: 1200px) {
                display: none;
            }
        }
        hr {
            border-color: #a1a1a1;
            margin: 30px 0;
        }
        label.features {
            margin-top: 30px;
            padding-left: 23px;
            display: block;
            margin-bottom: 10px;
            font-size: 12px;
        }
        .el-checkbox-group {
            display: flex;
            flex-wrap: wrap;
            .el-checkbox {
                margin-left: 0;
                width: 50%;
                .el-checkbox__label {
                    color: #fff;
                }
            }
        }
        .el-checkbox__inner {
            border-radius: 50%;
        }
        form {
            .block {
                margin-top: 15px;
            }

            input[type="text"],
            input[type="number"] {
                -webkit-appearance: none;
                -moz-appearance: none;
                appearance: none;
            }

            input[type='text']:focus,
            input[type='number']:focus,
            textarea:focus {
                font-size: 13px;
            }

            /* disable auto-zoom on iphone input field focus */
            @media screen and (-webkit-min-device-pixel-ratio:0) {

                select:focus,
                textarea:focus,
                input:focus {
                    font-size: 13px;
                }
            }

            @media (max-width: 767px) {

                // prevents phone from zooming in
                input[type="text"],
                input[type="number"] {
                    font-size: 12px;
                }
            }
        }
        .el-slider__button {
            top: -5%;
        }
        .el-input__inner {
            background: transparent;
            border: 1px solid #fff;
            color: #fff;
            border-radius: 0;
            font-size: 12px;
            font-family: inherit;
        }
        .el-checkbox__label {
            font-size: 12px;
        }
        .el-input, .el-select {
            width: 100%;
            max-width: 100%;
        }
        ::-webkit-input-placeholder {
            opacity: 1 !important;
            color: #fff !important;
        }
        :-moz-placeholder { /* Firefox 18- */
            opacity: 1 !important;
            color: #fff !important;
        }
        ::-moz-placeholder { /* Firefox 19+ */
            opacity: 1 !important;
            color: #fff !important;
        }
        :-ms-input-placeholder {
            opacity: 1 !important;
            color: #fff !important;
        }
    }

    .page {
        /*element UI custom*/
        .el-select-dropdown__item, .el-picker-panel {
            font-family: $font-family-body;
            font-size: 12px;
        }
        .el-date-table td.current:not(.disabled) span {
            background-color: $theme-green;
        }
        .el-date-table td.available:hover {
            color: $theme-green;
        }
        .el-date-table td.today span {
            color: $theme-green;
        }
        .el-date-picker__header-label.active,
        .el-date-picker__header-label:hover {
            color: $theme-green;
        }
        .el-picker-panel__icon-btn:hover {
            color: $theme-green;
        }
        .el-input.is-active .el-input__inner,
        .el-select .el-input__inner:focus,
        .el-select .el-input.is-focus .el-input__inner {
            border-color: $theme-green;
        }
        .el-select-dropdown__item.selected {
            color: $theme-green;
        }
    }
</style>